/**
 * iiif-comparison
 * 
 * A webcomponent that provides a comparison UX between multiple IIIF images.
 * Mostly this is just a `lit` wrapper over OpenSeaDragon and an adaptation of OpenSeaDragon-Curtainsync
 * 
 * TODO:
 * - [x] Basic component design + boilerplate
 *  - Array of `images`, { label: "This Image's Button Label Text", tileSource: "" } 
 * - [x] OSD initialize - use https://dlc.services/iiif-img/48/11/rsperberjstenger-fig38-left.jpg/info.json vs https://dlc.services/iiif-img/48/11/rsperberjstenger-fig38-right.jpg/info.json 
 * - [x] Test one-up -- give `viewer` a height directive to ensure getBoundingClientRect() (etc..) has height
 * - [x] Init and attach Curtainsync
 * - [x] Test two-up
 * - [x] Take args from init
 * - [ ] Styles etc and final UI / UX
 * 
 **/

import { css, html, LitElement } from 'lit'
import { createRef, ref } from 'lit/directives/ref.js'
import CurtainSyncViewer from './openseadragon-curtainsync.js'

const CONTROLS_WIDTH_THRESHOLD = 400 

class OpenSeaDragonComparison extends LitElement {
  static properties = {
    images: {
      type: Array,
      state: true
    },
    mode: {
      type: String,
      state: true,
    },
    showControls: {
      type: Boolean,
      state: true
    },
    showingImages: {
      type: Array,
      state: true
    },
    width: {
      type: Number,
      state: true
    }
  }

  static styles = css`
    :host {
      display: inline-block;
      height: 70vh;
      width: 50vw;
      margin: 0 .25rem;
      padding: 0 .25rem;
    }

    .viewer-frame {
      display: flex; 
      width: 100%; 
      height: 100%; 
      margin: 0;
    }

    .viewer-container {
      width: 100%; 
      height: 100%; 
    }

    .viewer-controls {
      position: absolute;
      display: flex;
      margin: 0 0.5rem;
      padding-top: 1rem;
      width: 75%;

      &.hidden {
        display: none;
      }

      .controls {
        margin: 0 0.5rem;

        label {
          color: var(--body-bg-color);
          font-family: var(--arial-sans);
          font-size: .625rem;
          letter-spacing: .125rem;
          margin-right: 0.5rem;
        }
      }
    }

    .btn {
      appearance: none;
      background: none;

      background-color: var(--body-bg-color);
      border: none;
      color: var(--theme-text-color);
      cursor: pointer;

      font-family: var(--arial-sans);
      font-size: .625rem;

      height: auto;
      letter-spacing: .125rem;

      margin-right: 0.25rem;
      max-height: 2.5rem;
      padding: .85rem;

      text-transform: uppercase;

      &:hover, &:active {
        background-color: var(--theme-text-color);
        color: var(--body-bg-color);
      }

      &.mode-trigger {

      }

      &.zoom-trigger {

      }

      &.active {
        background-color: var(--theme-text-color);
        color: var(--body-bg-color);
      }
    }
  `

  // Lifecycle management bits -- durable refs to container and the OSD instance
  viewerInst = undefined
  viewerRef = createRef()

  // Component subtree rendered so `viewerRef` points to our viewer: init OSD+curtainsync and check size
  firstUpdated() {
    this.viewerInst = new CurtainSyncViewer({container: this.viewerRef.value, images: this.images.map( (img,i) => { return { ...img, key: i, shown: true }} ) })

    // TODO: Remove this timeout (oof) and replace with something like this.updateComplete.then() 
    setTimeout(() => {
      this.handlePossibleComponentResize()
      this.requestUpdate()      
    },100)
  }

  // Native lifecycle -- connected but component children have not been rendered yet
  connectedCallback() {
    super.connectedCallback()
  }

  // Native lifecycle -- merely initialized
  constructor() {
    super()

    this.images = this.hasAttribute('images') ? JSON.parse(this.getAttribute('images')) : [] 
    this.mode = 'curtain'
    this.showControls = this.attributes.showControls ?? true
    this.showingImages = Array(this.images.length).fill(true)

    window.addEventListener('resize', () => this.handlePossibleComponentResize() )
  }

  handleZoomIn() {
    if (!this.viewerInst) return

    this.viewerInst.zoomIn()
  }

  handleZoomOut() {
    if (!this.viewerInst) return

    this.viewerInst.zoomOut()
  }

  handleToggleViewImage(key) {
    if (!this.viewerInst) return

    // Turn off images that are not this one
    this.images.forEach( (img,i) => {
      if (i === key) {
        this.viewerInst.setImageShown(key,!this.viewerInst.getImageShown(key))
      }
    })

    this.showingImages = [ ...this.showingImages.map( (s,i) => i === key ? !s : s ) ]
  }

  handleModeChange(mode) {
    if (!this.viewerInst) return

    this.mode = mode
    this.viewerInst.setMode(mode)
  }

  handlePossibleComponentResize() {
    const { width } = this.getBoundingClientRect()
    const { devicePixelRatio } = window

    if (!(width > 0) || !(devicePixelRatio > 0)) return

    this.width = width / devicePixelRatio

    if (this.width < CONTROLS_WIDTH_THRESHOLD) {
      this.showControls = false
      return
    }

    this.showControls = true
  }

  renderViewButton(image,key,active) {
    return html`<button @click=${ () => this.handleToggleViewImage(key) } class="btn view-trigger ${ active ? 'active' : '' }" data-type="${ key }" title="${ image.label }">${ image.label }</button>`
  }

  render() {
    const { width } = this.getBoundingClientRect()

    return html`<div class="viewer-frame">
                <!-- Viewer Element -->
                <div class="viewer-container" ${ref(this.viewerRef)} id="viewer"></div>
                <nav class="viewer-controls ${ this.showControls ? '' : 'hidden' }">
                  <!-- Zoom controls -->
                  <div class="controls">
                    <label>Zoom</label>
                    <button @click=${ () => this.handleZoomIn() } class="btn zoom-trigger" data-type="in" title="Zoom In">Zoom In</button>
                    <button @click=${ () => this.handleZoomOut() } class="btn zoom-trigger" data-type="out" title="Zoom Out">Zoom Out</button>
                  </div>

                  <!-- Type controls -->
                  <div class="controls">
                    <label>Views</label>
                    ${ this.images.map( (img,i) => this.renderViewButton(img,i,this.showingImages[i]) ) }
                  </div>

                  <!-- Mode toggles -->
                  <div class="controls">
                    <label>Modes</label>
                    <button @click=${ () => this.handleModeChange('curtain') } class="btn ${ this.mode === 'curtain' ? 'active' : '' } mode-trigger mode-curtain" data-mode="curtain" title="Curtain Mode">Curtain</button>
                    <button @click=${ () => this.handleModeChange('sync') } class="btn ${ this.mode === 'sync' ? 'active' : '' } mode-trigger mode-syncs" data-mode="sync" title="Sync Mode">Sync</button>
                  </div>
                </nav>
                </div>`
  }
}

customElements.define('iiif-comparison', OpenSeaDragonComparison)